import { createThunk, model, Model } from 'mst-collection';
import { castToSnapshot, getRoot, types } from 'mobx-state-tree';

import { MessageListModel } from './MessageListModel';
import { UserModel } from '../Viewer/UserModel';
import type { RootModelType } from '../RootModel';
import {
  Api,
  IBaseMessageResponse,
  INewMessagesCountResponse,
  ISendMessageRequestParams,
} from '@api';
import { trim } from '@utils';

type SendMessageParamsType = Pick<
  ISendMessageRequestParams,
  'message'
>;

export class Conversation extends Model({
  id: types.identifierNumber,
  user: types.reference(UserModel),
  messages: types.optional(MessageListModel, {}),
  newCount: types.number,
  lastMessage: types.maybeNull(types.string),
}) {
  private get _currentUserId(): number {
    return getRoot<RootModelType>(this).viewer.currentUser!.id;
  }

  get hasNewMessages() {
    return this.newCount > 0;
  }

  get formattedNewCount() {
    return this.newCount > 9 ? '9+' : `${this.newCount}`;
  }

  clearNewCount() {
    getRoot<RootModelType>(this).chats.decreaseNewCount();

    this.newCount = 0;
  }

  setNewDetails(
    params: Omit<INewMessagesCountResponse, 'conversationId'>,
  ) {
    this.newCount = params.newCount;
    this.lastMessage = params.lastMessage;
  }

  addNewMessage(message: IBaseMessageResponse) {
    this.messages.addToBegin(castToSnapshot(message));
  }

  sendMessage = createThunk((params: SendMessageParamsType) => {
    return async function sendMessage(this: Conversation) {
      const messageData = {
        id: Date.now(),
        userId: this._currentUserId,
        message: trim(params.message, true),
        sendTime: new Date().toISOString(),
        isIncome: false,
      };

      try {
        this.addNewMessage(messageData);

        const res = await Api.Messaging.sendMessage({
          recipientId: this.user.id,
          sendTime: messageData.sendTime,
          message: messageData.message,
        });

        this.messages.replace(
          messageData.id,
          castToSnapshot({
            ...messageData,
            id: res.data.addedMessageId,
          }),
        );
      } catch (error) {
        // Assign a sending error to the message
        this.messages.findById(messageData.id)?.setHasError(true);

        throw error;
      }
    };
  });

  markAsRead = createThunk(() => {
    return async function markAsRead(this: Conversation) {
      this.clearNewCount();
      await Api.Messaging.setAsRead({ id: this.id });
    };
  });
}

export const ConversationModel = model(Conversation);
